import { OrderResponse } from '@payments-checkout/shared/helpers/constants/order-status.const';

import {
  usePaymentMethod,
  useSessionId,
} from '@payments-checkout/shared/providers';
import { UseMutationOptions, useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useParams } from 'next/navigation';
import { useCustomerData } from '../../hooks/useCustomerData';
import { sendPayment } from '../../hooks/usePayment';
import { useSendBillingAddress } from '../../hooks/useSendBillingAddress';
import { acceptedPaymentMethod } from '../../types/customer.types';
import { googlePayPayloadBuilder } from './googlePayPayloadBuilder.helpers';

type PaymentDataArrayProps = {
  payment_method: acceptedPaymentMethod;
  credit_card?: {
    token_id?: string;
    installments?: number | null;
    antifraud_id?: string | null;
  };
};

export type PaymentDataProps = {
  payments: Array<PaymentDataArrayProps>;
};

type UseGooglePaymentMutationProps = {
  paymentData: google.payments.api.PaymentData;
  paymentInfo: { payments: Array<any> };
};

export const useGooglePayment = (
  options?: Omit<
    UseMutationOptions<
      OrderResponse,
      AxiosError,
      UseGooglePaymentMutationProps,
      unknown
    >,
    'mutateFn'
  >
) => {
  const { paymentLinkId } = useParams<{ paymentLinkId: string }>();
  const { sessionId } = useSessionId();

  const { mutateAsync: sendCustomerData } = useCustomerData();
  const { mutateAsync: sendBillingAddress } = useSendBillingAddress();
  const { digitalWalletDocument, digitalWalletPhone } = usePaymentMethod();

  return useMutation({
    mutationFn: async ({
      paymentData,
      paymentInfo,
    }: UseGooglePaymentMutationProps) => {
      const { address } =
        googlePayPayloadBuilder.sendBillingAddressPayloadBuilder(paymentData);
      const customerData =
        googlePayPayloadBuilder.sendCustomerDataPayloadBuilder(
          paymentData,
          paymentLinkId,
          digitalWalletDocument,
          digitalWalletPhone,
          sessionId
        );
      await sendCustomerData({
        ...customerData,
      });

      await sendBillingAddress({
        address,
      });

      return sendPayment(paymentLinkId, paymentInfo, sessionId);
    },
    ...options,
  });
};
